import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import {
  IArnoDecisionTemplate,
  useArnoTemplateFlow,
} from '../../hooks/reactQuerys/ArnoAdminQueries';
import {
  countNodes,
  flowKindColor,
  flowSummary,
  FLOW_KIND_LABEL,
  IFlowNode,
  isDecisionPoint,
  technicalEntries,
} from './flowPresentation';
import { TechnicalDetails } from './TechnicalDetails';
import { templateSourceChip } from './arnoAdminLabels';

/**
 * Ablauf eines Entscheidungsbaums, nur lesend (#1245).
 *
 * Dieselbe Darstellung für importierte und für in DiNo angelegte Bäume. Sie liest
 * das kanonische Modell, nicht ArnoTop: eine Frage sieht gleich aus, ob dahinter
 * ein Register 90 steht oder eine native Bedingung. Opcode, Register und
 * Rohanweisung stehen je Knoten hinter „Technische Details".
 *
 * Bewusst kein Editor: hier wird nichts verändert und nichts ausgewertet. Es
 * werden alle Zweige gezeigt, nicht der für bestimmte Antworten gültige.
 */

interface Props {
  template: IArnoDecisionTemplate | null;
  onClose: () => void;
}

const FlowNode: React.FC<{ node: IFlowNode; depth: number }> = ({
  node,
  depth,
}) => {
  const children = node.children ?? [];
  const technical = technicalEntries(node.technical);
  const decision = isDecisionPoint(node.kind);

  return (
    <Box
      sx={{
        pl: depth === 0 ? 0 : 2,
        borderLeft: depth === 0 ? 0 : 1,
        borderColor: 'divider',
        py: 0.5,
      }}
    >
      <Stack direction='row' spacing={1} alignItems='flex-start'>
        <Chip
          size='small'
          color={flowKindColor(node.kind)}
          variant={decision ? 'filled' : 'outlined'}
          label={FLOW_KIND_LABEL[node.kind]}
          sx={{ minWidth: 92 }}
        />
        <Box sx={{ flexGrow: 1 }}>
          {node.condition && (
            <Typography variant='caption' color='primary'>
              {node.condition}
            </Typography>
          )}
          <Typography
            variant='body2'
            sx={{ fontWeight: decision || node.kind === 'section' ? 600 : 400 }}
          >
            {node.title}
          </Typography>
          {node.detail && (
            <Typography variant='caption' color='text.secondary'>
              {node.detail}
            </Typography>
          )}
          {node.note && (
            <Typography variant='caption' color='warning.main' display='block'>
              {node.note}
            </Typography>
          )}
          {technical.length > 0 && (
            <TechnicalDetails
              entries={technical.map(entry => ({
                label: entry.label,
                value: entry.value,
              }))}
            />
          )}
        </Box>
      </Stack>

      {children.map((child, index) => (
        <FlowNode
          key={`${child.kind}-${child.title}-${index}`}
          node={child}
          depth={depth + 1}
        />
      ))}
    </Box>
  );
};

export const TemplateFlowDialog: React.FC<Props> = ({ template, onClose }) => {
  const [variant, setVariant] = useState('mGB');
  const { templateFlow, isLoadingTemplateFlow } = useArnoTemplateFlow(
    template?.idDecisionTemplate ?? null,
    variant
  );

  const source = template ? templateSourceChip(template.sourceType) : null;
  const variants = template?.variants.length ? template.variants : ['mGB'];

  return (
    <Dialog
      open={Boolean(template)}
      onClose={onClose}
      maxWidth='lg'
      fullWidth
      scroll='paper'
    >
      <DialogTitle>
        <Stack direction='row' spacing={1} alignItems='center'>
          <Typography variant='h6' sx={{ flexGrow: 1 }}>
            {template?.name}
          </Typography>
          {source && (
            <Chip size='small' color={source.color} label={source.label} />
          )}
          {variants.length > 1 && (
            <TextField
              size='small'
              select
              label='Variante'
              value={variant}
              onChange={event => setVariant(event.target.value)}
              sx={{ minWidth: 120 }}
            >
              {variants.map(item => (
                <MenuItem key={item} value={item}>
                  {item}
                </MenuItem>
              ))}
            </TextField>
          )}
        </Stack>
        <Typography variant='caption' color='text.secondary'>
          {flowSummary(templateFlow)}
        </Typography>
      </DialogTitle>

      <DialogContent dividers>
        <Alert severity='info' sx={{ mb: 2 }}>
          Nur zum Ansehen. Gezeigt werden alle Zweige, nicht der für bestimmte
          Antworten gültige — was tatsächlich im Dokument landet, entscheidet
          sich erst beim Erzeugen.
        </Alert>

        {isLoadingTemplateFlow ? (
          <Stack alignItems='center' sx={{ py: 6 }}>
            <CircularProgress />
          </Stack>
        ) : !templateFlow || templateFlow.sections.length === 0 ? (
          <Box sx={{ py: 6, textAlign: 'center' }}>
            <Typography variant='body2' color='text.secondary'>
              Dieser Entscheidungsbaum hat noch keine Abschnitte.
            </Typography>
          </Box>
        ) : (
          <Box>
            {templateFlow.sections.map(section => (
              <Box key={`${section.order}-${section.title}`} sx={{ mb: 2 }}>
                <FlowNode node={section} depth={0} />
              </Box>
            ))}
            <Typography variant='caption' color='text.secondary'>
              {countNodes(templateFlow.sections)} Einträge dargestellt; tiefere
              Verschachtelung ist ausgeblendet.
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Schließen</Button>
      </DialogActions>
    </Dialog>
  );
};
